import React, { Component } from "react";
import BrowserResizeDetector from "presentational/common/BrowserResizeDetector";
import config from "lib/fr-config";
import PropTypes from "prop-types";

const desktopCss = config.css.desktop;

const LEFT_SIDE_WIDTH_PERCENT = 50;
const LEFT_SIDE_PADDING_PERCENT = 6;
const RIGHT_SIDE_WIDTH_PERCENT =
  100 - LEFT_SIDE_WIDTH_PERCENT - LEFT_SIDE_PADDING_PERCENT;

/**
 * FIXED_HEIGHT_VALUE: 공용 top margin 값
 * OFFSET_HEIGHT_VALUE: 해당 컴포넌트 전용 마이너스 margin 값
 * NORMAL_BOTTOM_MARGIN_VALUE: 위의 값만 사용하면 컨탠츠가 바닥에 딱붙기에, 하단 마진을 위해 설정한 일정 마진값.
 */
const FIXED_HEIGHT_VALUE =
  (desktopCss.headerBarHeightPX +
    desktopCss.paddingBetweenHeaderbarAndFirstContentForSplitView) *
  2;
const OFFSET_HEIGHT_VALUE =
  -desktopCss.paddingBetweenHeaderbarAndFirstContent +
  desktopCss.paddingBetweenHeaderbarAndFirstContentForSplitView;
const NORMAL_BOTTOM_MARGIN_VALUE = 70;

class CommonSplitView extends Component {
  static propTypes = {
    /** getWidthOfLeftSide: 해당 컴포넌트를 사용하는 컴포넌트의 left side의 폭을 가져오는 함수 */
    getWidthOfLeftSide: PropTypes.func.isRequired,

    leftSideComponent: PropTypes.object.isRequired,
    rightSideComponent: PropTypes.object.isRequired
  };

  state = {
    widthOfContent: null,
    widthOfLeftSide: null,
    isSmallestHeightOfViewport: false
  };

  getCss() {
    const { widthOfContent, widthOfLeftSide, isSmallestHeightOfViewport } =
      this.state;

    return {
      root: { marginTop: OFFSET_HEIGHT_VALUE },
      contentArea: {
        margin: "auto",
        display: "flex",
        justifyContent: "space-between",
        width: widthOfContent || undefined
      },
      leftSide: {
        width: `${LEFT_SIDE_WIDTH_PERCENT}%`
      },
      floatArea: {
        position:
          widthOfLeftSide && !isSmallestHeightOfViewport ? "fixed" : "relative",
        width: widthOfLeftSide
      },
      rightSide: {
        width: `${RIGHT_SIDE_WIDTH_PERCENT}%`
      }
    };
  }

  handleResize = () => {
    const _getLeftSideWidthOfMaxViewport = () => {
      const { innerWidth } = window;
      const frameWidth = innerWidth - 2 * desktopCss.sidePadding;
      return (frameWidth * LEFT_SIDE_WIDTH_PERCENT) / 100;
    };

    const _getMaxWidthOfLeftSide = () => {
      const maxHeaderbarWidth = desktopCss.maximumWidth;
      const frameWidth = maxHeaderbarWidth - 2 * desktopCss.sidePadding;
      return (frameWidth * LEFT_SIDE_WIDTH_PERCENT) / 100;
    };

    const _getMinWidthOfLeftSide = () => {
      const minHeaderbarWidth = desktopCss.minimumWidth;
      const frameWidth = minHeaderbarWidth - 2 * desktopCss.sidePadding;
      return (frameWidth * LEFT_SIDE_WIDTH_PERCENT) / 100;
    };

    const { getWidthOfLeftSide } = this.props;
    const { widthOfLeftSide } = this.state;
    const nowWidthOfLeftSide = getWidthOfLeftSide(
      FIXED_HEIGHT_VALUE + OFFSET_HEIGHT_VALUE + NORMAL_BOTTOM_MARGIN_VALUE
    );
    const maxWidthOfLeftSide1 = _getLeftSideWidthOfMaxViewport();
    const maxWidthOfLeftSide2 = _getMaxWidthOfLeftSide();
    const minWidthOfLeftSide = _getMinWidthOfLeftSide();

    const newWidthOfLeftSide = Math.max(
      !!nowWidthOfLeftSide
        ? Math.min(nowWidthOfLeftSide, maxWidthOfLeftSide1, maxWidthOfLeftSide2)
        : Math.min(maxWidthOfLeftSide1, maxWidthOfLeftSide2),
      minWidthOfLeftSide
    );

    if (
      widthOfLeftSide !== newWidthOfLeftSide &&
      Math.abs(widthOfLeftSide - newWidthOfLeftSide) > 1
    ) {
      /**
       * 특정상황에서 계속 인근의 값으로 변경되며 무한루프 발생하여 2px이상 차이날경우에만 실행되도록 수정.
       * (주석 수정사항) 조건문 내 "&& Math.abs(widthOfLeftSide - newWidthOfLeftSide) > 1"
       * 문장때문에 천천히 브라우저 뷰포트를 줄이다보면 원래는 isSmallestHeightOfViewport변수가 true가
       * 되면서 왼쪽화면 css의 fixed 효과가 없어져야 되는데 안없어지는 현상 발생되어 계속 떠다니는 오류 발생되지만,
       * 계속 떠다니는 오류는 치명적이지 않다고 현재 판단됨. 반면 해당 문장 지워서 발생하는 무한루프로 인한
       * 연산 반복되는 상황은 발생시키기 어려웠지만 이 버그는 상대적으로 치명적이기에 그냥 냅둠.
       */
      this.setState({
        widthOfContent: (newWidthOfLeftSide * 100) / LEFT_SIDE_WIDTH_PERCENT,
        widthOfLeftSide: newWidthOfLeftSide,
        isSmallestHeightOfViewport: newWidthOfLeftSide === minWidthOfLeftSide
      });
    }
  };

  render() {
    const { leftSideComponent, rightSideComponent } = this.props;
    const css = this.getCss();
    return (
      <div style={css.root}>
        <BrowserResizeDetector onResize={this.handleResize} />
        <div style={css.contentArea}>
          <div style={css.leftSide}>
            <div style={css.floatArea}>{leftSideComponent}</div>
          </div>
          <div style={css.rightSide}>{rightSideComponent}</div>
        </div>
      </div>
    );
  }

  componentDidMount() {
    this.handleResize();
  }

  componentDidUpdate() {
    this.handleResize();
  }
}

export default CommonSplitView;
